import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, CalendarDays, DollarSign } from "lucide-react";
import Reveal from "./Reveal";
import { IMAGES } from "../../data/brand";

const CARDS = [
  {
    icon: MapPin,
    label: "Location",
    value: "Anytime Fitness",
    sub: "Halifax, Nova Scotia",
  },
  {
    icon: CalendarDays,
    label: "Format",
    value: "15 Sessions",
    sub: "Spread out for rest & recovery",
  },
  {
    icon: DollarSign,
    label: "Investment",
    value: "$600 + Tax",
    sub: "Just $40 per session",
  },
];

export default function Logistics() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-10%", "20%"]);

  return (
    <section
      ref={ref}
      data-testid="logistics-section"
      className="relative py-28 sm:py-40 px-5 sm:px-8 overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.gym}
          alt="Gym interior"
          className="w-full h-full object-cover opacity-[0.12]"
        />
      </motion.div>
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />

      <div className="relative z-10 max-w-[1400px] mx-auto">
        <Reveal>
          <div className="mb-16 max-w-2xl">
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#d4af37] mb-5">
              The Details
            </p>
            <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Expert coaching for a<br />
              <span className="gold-text">fraction of 1-on-1</span>
            </h2>
            <p className="mt-6 text-lg font-light text-neutral-300 leading-relaxed">
              At $40 a session you get hands-on coaching, real-time form
              correction, and foundational nutrition principles — the same
              standards as private training, in a focused group setting. The 15
              sessions are intentionally spread out (not back-to-back) to build
              in rest and proper recovery.
            </p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-3 gap-5">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.label} delay={i * 0.12}>
                <div
                  data-testid={`logistics-card-${i}`}
                  className="group relative bg-[#111111] border border-white/10 p-8 h-full transition-all duration-500 hover:border-[#d4af37]/50 hover:-translate-y-1"
                >
                  <Icon className="w-7 h-7 text-[#d4af37] mb-8" strokeWidth={1.5} />
                  <p className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-2">
                    {c.label}
                  </p>
                  <p className="font-display font-bold text-2xl sm:text-3xl mb-1">
                    {c.value}
                  </p>
                  <p className="text-neutral-400 font-light">{c.sub}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
